import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EmailIcon from '@mui/icons-material/Email';
import PaletteIcon from '@mui/icons-material/Palette';
import CheckIcon from '@mui/icons-material/Check';
import { PageLayout, PageSection, GlassCard } from '@paneon/ui';
import { useTheme } from '@paneon/theme';
import { useAuth } from '@paneon/auth';

/**
 * Settings page
 */
export function SettingsPage() {
  const { isDarkMode, setThemeMode, themeMode, currentTheme, availableThemes, setTheme } =
    useTheme();
  const { user, signOut } = useAuth();

  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [newsletterEnabled, setNewsletterEnabled] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(true);

  const handleDarkModeToggle = () => {
    setThemeMode(themeMode === 'dark' ? 'light' : 'dark');
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      // Navigation wird automatisch durch AuthProvider gesteuert
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleThemeSelect = (themeId: string) => {
    setTheme(themeId);
    setColorPickerOpen(false);
  };

  return (
    <PageLayout title="Einstellungen">
      {/* Profile Section */}
      <PageSection title="Profil">
        <GlassCard sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: alpha(isDarkMode ? '#fff' : '#000', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
              }}
            >
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.name || 'Avatar'}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                '👤'
              )}
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: isDarkMode ? '#fff' : '#000',
                }}
              >
                {user?.name || 'Benutzer'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6),
                }}
              >
                {user?.email}
              </Typography>
            </Box>
          </Box>
        </GlassCard>
      </PageSection>

      {/* Appearance Section */}
      <PageSection title="Erscheinungsbild">
        <SettingsItem
          icon={<DarkModeIcon />}
          title="Dunkelmodus"
          subtitle={themeMode === 'system' ? 'Systemeinstellung' : isDarkMode ? 'An' : 'Aus'}
          action={<Switch checked={isDarkMode} onChange={handleDarkModeToggle} color="primary" />}
        />
        <SettingsItem
          icon={<PaletteIcon sx={{ color: currentTheme.colors.primary }} />}
          title="Farbschema"
          subtitle={currentTheme.name}
          showArrow
          onClick={() => setColorPickerOpen(true)}
        />
      </PageSection>

      {/* Notifications Section */}
      <PageSection title="Benachrichtigungen">
        <SettingsItem
          icon={<NotificationsIcon />}
          title="Push-Benachrichtigungen"
          subtitle="Erinnerungen für Fütterungszeiten"
          action={
            <Switch
              checked={pushEnabled}
              onChange={e => setPushEnabled(e.target.checked)}
              color="primary"
            />
          }
        />
        <SettingsItem
          icon={<EmailIcon />}
          title="Newsletter & E-Mails"
          subtitle="Tipps, Updates und Neuigkeiten"
          action={
            <Switch
              checked={newsletterEnabled}
              onChange={e => setNewsletterEnabled(e.target.checked)}
              color="primary"
            />
          }
        />
      </PageSection>

      {/* Account Section */}
      <PageSection title="Konto">
        <SettingsItem
          icon={<LogoutIcon sx={{ color: '#d32f2f' }} />}
          title="Abmelden"
          titleColor="#d32f2f"
          onClick={handleSignOut}
        />
      </PageSection>

      {/* Version */}
      <Box sx={{ textAlign: 'center', mt: 4, mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            color: isDarkMode ? alpha('#fff', 0.4) : alpha('#000', 0.4),
          }}
        >
          Paneon Pets v0.2.0
        </Typography>
      </Box>

      {/* Color Picker Dialog */}
      <Dialog
        open={colorPickerOpen}
        onClose={() => setColorPickerOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 360,
            width: '100%',
            backgroundColor: isDarkMode ? '#1E1E1E' : '#fff',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: isDarkMode ? '#fff' : '#000',
          }}
        >
          Farbschema wählen
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <List>
            {availableThemes.map(theme => (
              <ListItem key={theme.id} disablePadding>
                <ListItemButton
                  onClick={() => handleThemeSelect(theme.id)}
                  sx={{
                    py: 1.5,
                    px: 3,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 48 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: theme.gradient,
                        border: currentTheme.id === theme.id ? '3px solid' : '2px solid',
                        borderColor:
                          currentTheme.id === theme.id
                            ? theme.colors.primary
                            : isDarkMode
                              ? alpha('#fff', 0.2)
                              : alpha('#000', 0.1),
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={theme.name}
                    primaryTypographyProps={{
                      fontWeight: currentTheme.id === theme.id ? 600 : 400,
                      color: isDarkMode ? '#fff' : '#000',
                    }}
                  />
                  {currentTheme.id === theme.id && (
                    <CheckIcon sx={{ color: theme.colors.primary }} />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}

/**
 * Settings item component
 */
function SettingsItem({
  icon,
  title,
  subtitle,
  action,
  onClick,
  showArrow,
  titleColor,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  onClick?: () => void;
  showArrow?: boolean;
  titleColor?: string;
}) {
  const { isDarkMode } = useTheme();

  return (
    <GlassCard
      onClick={onClick}
      sx={{
        p: 2,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          backgroundColor: alpha(isDarkMode ? '#fff' : '#000', 0.05),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 2,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 500,
            color: titleColor || (isDarkMode ? '#fff' : '#000'),
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? alpha('#fff', 0.6) : alpha('#000', 0.6),
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
      {showArrow && (
        <Typography
          sx={{
            color: isDarkMode ? alpha('#fff', 0.4) : alpha('#000', 0.4),
            ml: 1,
          }}
        >
          ›
        </Typography>
      )}
    </GlassCard>
  );
}
